import { Box } from "@rebass/grid";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as paymentOrderActions from "@rentiohq/shared-frontend/dist/reduxV2/paymentOrder/paymentOrder.actions";
import * as paymentOrderHooks from "@rentiohq/shared-frontend/dist/reduxV2/paymentOrder/paymentOrder.hooks";
import * as paymentOrderUtils from "@rentiohq/shared-frontend/dist/reduxV2/paymentOrder/paymentOrder.utils";
import * as propertyHooks from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import { IPaymentOrder } from "@rentiohq/shared-frontend/dist/types/payment.types";
import { confirm } from "@rentiohq/shared-frontend/dist/utils/confirm.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  canAddPaymentOrders,
  groupPaymentOrdersByContractV2,
} from "@rentiohq/shared-frontend/dist/utils/paymentOrder.utils";
import {
  Button,
  Card,
  DisplayText,
  ESpacings,
  EmptyState,
  Error,
  Grid,
  Loading,
  RentioInternalRendererPaymentGroup,
  ResourceList,
  Washout,
} from "@rentiohq/web-shared/dist/components";
import isEmpty from "ramda/es/isEmpty";
import React from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as t from "../../../../../../../services/translationService";
import { ContractInfoCardFooter } from "../../../components/PayInsFollowUp/ContractInfoCardFooterV2";
import { PaymentGroupErrorBanner } from "../PaymentGroupErrorBanner";
import { PaymentOrderRow } from "./../PaymentOrderRow";

interface IProps {
  propertyId: number;
}

export const PropertyPaymentOrders = (props: IProps) => {
  const { propertyId } = props;

  const navigate = useNavigate();
  const [showInactive, setShowInactive] = React.useState<boolean>(false);

  // Redux
  const dispatch = useDispatch();

  const { isBroker } = authHooks.useSelf();

  const { detail: property } = propertyHooks.useDetail({
    shouldRefetch: false,
    id: propertyId,
  });

  const activePaymentOrdersQuery = paymentOrderUtils.getPaymentOrdersQuery({
    propertyId,
    filter: { completedAt: { is: null } },
  });
  const {
    items: activePaymentOrders,
    isFetching: isFetchingActivePaymentOrders,
    fetchError: fetchErrorActivePaymentOrders,
  } = paymentOrderHooks.useGetAll({
    query: activePaymentOrdersQuery,
    shouldRefetch: true,
  });

  const inactivePaymentOrdersQuery = paymentOrderUtils.getPaymentOrdersQuery({
    propertyId,
    filter: { completedAt: { isNot: null } },
  });
  const { count: inactivePaymentOrdersCount = 0 } = paymentOrderHooks.useCount({
    query: inactivePaymentOrdersQuery,
    shouldRefetch: true,
  });
  const {
    items: inactivePaymentOrders,
    isFetching: isFetchingInactivePaymentOrders,
    fetchError: fetchErrorInactivePaymentOrders,
  } = paymentOrderHooks.useGetAll({
    query: showInactive ? inactivePaymentOrdersQuery : undefined,
    shouldRefetch: true,
  });

  // Lifecycle
  const groupedPaymentOrders = groupPaymentOrdersByContractV2(
    activePaymentOrders || [],
  );

  const groupedInactivePaymentOrders = inactivePaymentOrders
    ? groupPaymentOrdersByContractV2(inactivePaymentOrders)
    : undefined;

  // Event handlers
  const handleRemoveWithConfirm = (paymentOrderId: number) => {
    const handleRemove = (paymentOrderId: number) => () => {
      dispatch(
        paymentOrderActions.removeStart.getAction({ id: paymentOrderId }),
      );
    };

    confirm({
      title: t.removeConfirm("payment_order"),
      primaryActions: [
        {
          title: t.system("remove"),
          onPress: handleRemove(paymentOrderId),
        },
      ],
    });
  };

  const handleEdit = (paymentOrder: IPaymentOrder) => {
    navigate(`/properties/${propertyId}/payments-v2/${paymentOrder.id}/edit`);
  };

  const handleShowInactiveClick = () => {
    setShowInactive(prevResult => !prevResult);
  };

  // Render
  if (!property) {
    return null;
  }

  const renderContent = () => {
    if (isFetchingActivePaymentOrders) {
      return (
        <Card hasSections={true}>
          <Loading />
        </Card>
      );
    }

    if (!activePaymentOrders) {
      if (fetchErrorActivePaymentOrders) {
        return (
          <Card hasSections={true}>
            <Error errors={[fetchErrorActivePaymentOrders]} />
          </Card>
        );
      }

      return null;
    }

    if (activePaymentOrders.length === 0 && inactivePaymentOrdersCount === 0) {
      if (!canAddPaymentOrders({ property, isBroker })) {
        return null;
      }

      return (
        <Card hasSections={true}>
          <EmptyState
            size="large"
            heading={t.paymentsOverviewEmptyStateTitle()}
            backgroundColor="white"
            action={{
              content: t.paymentsOverviewEmptyStatePrimaryCTA(),
              url: `/properties/${propertyId}/payments-v2/add`,
            }}
          >
            <p>{t.paymentsOverviewEmptyStateContent()}</p>
          </EmptyState>
        </Card>
      );
    }

    const renderPaymentOrder = (paymentOrder: IPaymentOrder) => (
      <PaymentOrderRow
        paymentOrder={paymentOrder}
        onEdit={handleEdit}
        onRemove={handleRemoveWithConfirm}
      />
    );

    return (
      <>
        {isEmpty(activePaymentOrders) ? (
          <>
            <p>{t.paymentsOverviewEmptyStateNoResultContent()}</p>
          </>
        ) : (
          groupedPaymentOrders.map(group => (
            <Card
              footerContent={
                <ContractInfoCardFooter
                  contractId={group.contractInfo?.id}
                  showIbanisationInfo={false}
                />
              }
            >
              {group.paymentOrders.map(group2 => (
                <>
                  <RentioInternalRendererPaymentGroup
                    paymentGroupId={group2.paymentGroupId}
                  />

                  <PaymentGroupErrorBanner
                    paymentGroupId={group2.paymentGroupId}
                  />

                  <ResourceList
                    items={group2.paymentOrders}
                    renderItem={renderPaymentOrder}
                  />
                </>
              ))}
            </Card>
          ))
        )}

        {inactivePaymentOrdersCount > 0 && !showInactive && (
          <Box my={ESpacings.loose}>
            <Button appearance="link" onClick={handleShowInactiveClick}>
              {getLocalizedText("payments.overview.show_inactive.action", {
                value: `${inactivePaymentOrdersCount}`,
              })}
            </Button>
          </Box>
        )}

        {showInactive && (
          <Box my={ESpacings.loose}>
            <DisplayText size="medium">
              {t.paymentsOverviewInactiveHeading()}
            </DisplayText>

            {groupedInactivePaymentOrders &&
              groupedInactivePaymentOrders.length > 0 && (
                <Washout variation="medium">
                  {groupedInactivePaymentOrders.map(group => (
                    <Card
                      footerContent={
                        <ContractInfoCardFooter
                          contractId={group.contractInfo?.id}
                          showIbanisationInfo={false}
                        />
                      }
                    >
                      {group.paymentOrders.map(group2 => (
                        <>
                          <RentioInternalRendererPaymentGroup
                            paymentGroupId={group2.paymentGroupId}
                          />

                          <ResourceList
                            items={group2.paymentOrders}
                            renderItem={renderPaymentOrder}
                          />
                        </>
                      ))}
                    </Card>
                  ))}
                </Washout>
              )}

            {isFetchingInactivePaymentOrders && <Loading asDots />}

            {!isFetchingInactivePaymentOrders &&
              fetchErrorInactivePaymentOrders && (
                <Card hasSections={true}>
                  <Error errors={[fetchErrorInactivePaymentOrders]} />
                </Card>
              )}

            <Box mt={ESpacings.loose}>
              <Button appearance="link" onClick={handleShowInactiveClick}>
                {getLocalizedText(
                  "payments.overview.hide_inactive_contract.action",
                )}
              </Button>
            </Box>
          </Box>
        )}
      </>
    );
  };

  return (
    <>
      <Grid
        mb={ESpacings.base}
        alignItems="center"
        justifyContent="space-between"
      >
        <Grid.Item>
          <DisplayText size="medium">
            {getLocalizedText("payment_orders.title")}
          </DisplayText>
        </Grid.Item>

        {canAddPaymentOrders({ property, isBroker }) && (
          <Grid.Item>
            <Button
              appearance="primary"
              size="small"
              url={`/properties/${property.id}/payments-v2/add`}
            >
              {t.paymentsOverviewCreatePrimaryCTA()}
            </Button>
          </Grid.Item>
        )}
      </Grid>

      {renderContent()}
    </>
  );
};
